// Proactive refresh on resume/online using the provided tokenManager
// Usage:
//   import { registerProactiveRefresh } from './proactiveRefresh';
//   const dispose = registerProactiveRefresh(getTokenManager(), { debounceMs: 400 });
//   // call dispose() if you need to remove handlers

import debug from 'debug';
import { PROACTIVE_REFRESH_DEBOUNCE_MS } from '../config/auth.config';

const debugProactive = debug('auth:proactive');
const debugError = debug('auth:proactive:error');

export function registerProactiveRefresh(tokenManager, { debounceMs = PROACTIVE_REFRESH_DEBOUNCE_MS } = {}) {
  if (!tokenManager || typeof tokenManager.getAuthData !== 'function') {
    debugError('Invalid tokenManager provided, proactive refresh disabled');
    return () => {};
  }
  
  debugProactive('Registering proactive refresh (debounce: %dms)', debounceMs);

  let timer = null;
  const schedule = () => {
    if (timer) {
      debugProactive('Clearing existing scheduled refresh');
      try { clearTimeout(timer); } catch { /* noop */ }
    }
    debugProactive('Scheduling proactive refresh in %dms', debounceMs);
    timer = setTimeout(() => {
      timer = null;
      try {
        const a = tokenManager.getAuthData?.() || null;
        if (a && tokenManager.shouldProactivelyRefresh?.(a)) {
          debugProactive('Token should be refreshed, initiating refresh');
          tokenManager.refreshSingleFlight?.().catch((error) => {
            debugError('Proactive refresh failed: %O', error);
            /* reactive flow will handle */
          });
        } else {
          debugProactive('Token does not need refresh (auth data: %s, should refresh: %s)', 
            !!a, 
            a ? tokenManager.shouldProactivelyRefresh?.(a) : 'N/A'
          );
        }
      } catch (error) {
        debugError('Error during proactive refresh check: %O', error);
      }
    }, debounceMs);
  };

  const onVisibility = () => {
    try {
      if (document.visibilityState === 'visible') {
        debugProactive('Page became visible, scheduling refresh');
        schedule();
      } else {
        debugProactive('Page visibility changed to: %s', document.visibilityState);
      }
    } catch (error) {
      debugError('Error in visibility handler: %O', error);
    }
  };
  const onOnline = () => {
    debugProactive('Network came online, scheduling refresh');
    schedule();
  };

  try { 
    document.addEventListener('visibilitychange', onVisibility);
    debugProactive('visibilitychange event listener registered');
  } catch (error) {
    debugError('Failed to register visibilitychange listener: %O', error);
  }
  
  try { 
    window.addEventListener('online', onOnline);
    debugProactive('online event listener registered');
  } catch (error) {
    debugError('Failed to register online listener: %O', error);
  }

  return () => {
    debugProactive('Disposing proactive refresh handlers');
    try { 
      document.removeEventListener('visibilitychange', onVisibility);
      debugProactive('visibilitychange listener removed');
    } catch { /* noop */ }
    try { 
      window.removeEventListener('online', onOnline);
      debugProactive('online listener removed');
    } catch { /* noop */ }
    if (timer) {
      try { 
        clearTimeout(timer);
        debugProactive('Pending refresh timer cleared');
      } catch { /* noop */ }
      timer = null;
    }
    debugProactive('Proactive refresh disposal complete');
  };
}
